import { useEffect, useState } from "react";
import {
  findAllAtecos,
} from "./app/lib/atecos/slice";
import { addBannerWithTimeout } from "./app/lib/centralizedBanner/slice";
import { keycloak } from "./app/lib/keycloak";
import {
  setKeycloakInitStatus,
  setKeycloakUserInfo,
  setRoles,
} from "./app/lib/keycloak/slice";
import { useCreatePreferencesMutation, useFindAllPreferencesQuery, useUpdatePreferencesMutation } from "./app/lib/preferences/api";
import { companyColumns, peopleColumns, targetListColumns, targetListCompanyColumns, targetListPersonColumns, toVerifyCompanyColumns, toVerifyPeopleColumns } from "./app/lib/preferences/dto";
import { useAppDispatch, useAppSelector } from "./app/lib/redux/hooks";
import { KeycloakRouter } from "./app/lib/router";
import {
  checkPreferences,
} from "./app/pages/database/dto";
import { ProjectChangeStatusModal } from "./app/pages/projects/changeStatusModal";
import { TargetListChangeStatusModal } from "./app/pages/targetList/changeStatusModal";
import { findMe } from "./app/pages/users/slice";
import { Spinner } from "./app/ui/molecules/spinner";
import { useFindSuspendedProjectsQuery } from "./features/projectApi";
import { useFindSuspendedTargetListQuery } from "./features/targetlistApi";

function App() {
  const dispatch = useAppDispatch();
  const usersState = useAppSelector((state) => state.users);
  const keycloakState = useAppSelector((state) => state.keycloak);

  const [showTargetListModal, setShowTargetListModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);

  const {
    data: preferencesResponse,
    isLoading: isLoadingPreferences,
  } = useFindAllPreferencesQuery(keycloak.subject || "", {
    skip: !keycloak.subject,
  });

  const {
    data: targetListResponse,
    isLoading: isLoadingTargetList,
    error: targetListError,
    isSuccess: targetListSuccess,
  } = useFindSuspendedTargetListQuery(void 0, {
    skip: !keycloak.token || !(keycloak.hasRealmRole("admin") || keycloak.hasRealmRole("team_leader")),
  });

  const {
    data: projectResponse,
    isLoading: isLoadingProject,
    error: projectError,
    isSuccess: projectSuccess,
  } = useFindSuspendedProjectsQuery(void 0, {
    skip: !keycloak.token || !(keycloak.hasRealmRole("admin") || keycloak.hasRealmRole("team_leader")) || targetListResponse?.total !== 0,
  });

  const [createPreferences] = useCreatePreferencesMutation();
  const [updatePreferences] = useUpdatePreferencesMutation();

  useEffect(() => {
    if (window.location.pathname === "/")
      window.location.replace(window.location.origin + "/dashboard");
  }, []);

  keycloak.onAuthSuccess = () => {
    keycloak.loadUserInfo().then((e) => dispatch(setKeycloakUserInfo(e)));
    if (keycloak.hasRealmRole("company")) {
      keycloak.logout({ redirectUri: window.location.origin });
    }
    dispatch(setRoles(keycloak.tokenParsed?.realm_access?.roles));
    dispatch(setKeycloakInitStatus("successfully"));
    dispatch(findAllAtecos());
    dispatch(findMe())
  };

  useEffect(() => {
    if (preferencesResponse && !isLoadingPreferences) {
      if (preferencesResponse.data.length === 0) {
        createPreferences({
          owner: "",
          personPreferences: peopleColumns,
          companyPreferences: companyColumns,
          targetListPreferences: targetListColumns,
          targetListCompanyPreferences: targetListCompanyColumns,
          targetListPersonPreferences: targetListPersonColumns,
          toVerifyBinPersonPreferences: peopleColumns,
          toVerifyBinCompanyPreferences: companyColumns,
        });
      }
      else if (
        !checkPreferences(preferencesResponse.data[0].companyPreferences, companyColumns) ||
        !checkPreferences(preferencesResponse.data[0].personPreferences, peopleColumns) ||
        !checkPreferences(preferencesResponse.data[0].targetListPreferences, targetListColumns) ||
        !checkPreferences(preferencesResponse.data[0].targetListCompanyPreferences, targetListCompanyColumns) ||
        !checkPreferences(preferencesResponse.data[0].targetListPersonPreferences, targetListPersonColumns) ||
        !checkPreferences(preferencesResponse.data[0].toVerifyBinPersonPreferences, toVerifyPeopleColumns) ||
        !checkPreferences(preferencesResponse.data[0].toVerifyBinCompanyPreferences, toVerifyCompanyColumns)
      ) {
        // Aggiorna le preferenze se non sono valide
        updatePreferences({
          data: {
            owner: preferencesResponse.data[0].owner!,
            personPreferences: checkPreferences(preferencesResponse.data[0].personPreferences, peopleColumns) ? preferencesResponse.data[0].personPreferences : peopleColumns,
            companyPreferences: checkPreferences(preferencesResponse.data[0].companyPreferences, companyColumns) ? preferencesResponse.data[0].companyPreferences : companyColumns,
            targetListPreferences: checkPreferences(preferencesResponse.data[0].targetListPreferences, targetListColumns) ? preferencesResponse.data[0].targetListPreferences : targetListColumns,
            targetListPersonPreferences: checkPreferences(preferencesResponse.data[0].targetListPersonPreferences, targetListPersonColumns) ? preferencesResponse.data[0].targetListPersonPreferences : targetListPersonColumns,
            targetListCompanyPreferences: checkPreferences(preferencesResponse.data[0].targetListCompanyPreferences, targetListCompanyColumns) ? preferencesResponse.data[0].targetListCompanyPreferences : targetListCompanyColumns,
            toVerifyBinPersonPreferences: checkPreferences(preferencesResponse.data[0].toVerifyBinPersonPreferences, toVerifyPeopleColumns) ? preferencesResponse.data[0].toVerifyBinPersonPreferences : toVerifyPeopleColumns,
            toVerifyBinCompanyPreferences: checkPreferences(preferencesResponse.data[0].toVerifyBinCompanyPreferences, toVerifyCompanyColumns) ? preferencesResponse.data[0].toVerifyBinCompanyPreferences : toVerifyCompanyColumns
          },
          id: preferencesResponse.data[0].id!,
        });
      }
    }
  }, [preferencesResponse, isLoadingPreferences, createPreferences, updatePreferences]);

  useEffect(() => {
    if (targetListError) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Failed to fetch suspended target lists"
      }));
    }
  }, [targetListError, dispatch]);

  useEffect(() => {
    if (targetListSuccess) {
      if (targetListResponse?.total !== 0) {
        setShowTargetListModal(true);
      }
    }
  }, [targetListSuccess, dispatch]);


  useEffect(() => {
    if (projectError) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Failed to fetch suspended projects"
      }));
    }
  }, [projectError, dispatch]);

  useEffect(() => {
    if (projectSuccess) {
      if (projectResponse?.total !== 0) {
        setShowProjectModal(true);
      }
    }
  }, [projectSuccess, dispatch]);

  useEffect(() => {
    if (targetListSuccess) {
      if (targetListResponse?.total !== 0) {
        setShowTargetListModal(true);
      }
    }
  }, [targetListSuccess, dispatch]);

  keycloak.onAuthRefreshError = () => {
    keycloak.logout();
  };

  if (
    !preferencesResponse ||
    isLoadingPreferences ||
    preferencesResponse.data[0]?.companyPreferences === undefined ||
    preferencesResponse.data[0]?.personPreferences === undefined ||
    preferencesResponse.data[0]?.targetListCompanyPreferences === undefined ||
    preferencesResponse.data[0]?.targetListPersonPreferences === undefined ||
    preferencesResponse.data[0]?.targetListPreferences === undefined
  ) {
    return <></>
  }

  if (
    keycloakState.keycloakInitStatus === "idle" ||
    isLoadingTargetList ||
    isLoadingProject ||
    usersState.findMeStatus === 'loading' ||
    isLoadingPreferences
  ) {
    return (
      <div className="w-full h-[100vh] flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="App">
      <KeycloakRouter />
      <TargetListChangeStatusModal response={targetListResponse} show={showTargetListModal} setShow={setShowTargetListModal} />
      <ProjectChangeStatusModal response={projectResponse} show={showProjectModal} setShow={setShowProjectModal} />
    </div>
  );
}

export default App;
